import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router";
import { useAuth, useClerk, useOrganizationList, useUser } from "@clerk/react";
import { setAuthTokenGetter } from "./api/client";
import { useAuditStore } from "./stores/useAuditStore";
import { Shell } from "./components/layout/Shell";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { DashboardPage } from "./pages/DashboardPage";
import { FamilyPage } from "./pages/FamilyPage";
import { DocumentEditorPage } from "./pages/DocumentEditorPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { TemplateFamiliesPage } from "./pages/TemplateFamiliesPage";
import { ReviewQueuePage } from "./pages/ReviewQueuePage";
import { AgentSuggestionsPage } from "./pages/AgentSuggestionsPage";
import { SnippetsPage } from "./pages/SnippetsPage";
import { PublishPage } from "./pages/PublishPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AuditPage } from "./pages/AuditPage";
import { SellSheetsPage } from "./pages/SellSheetsPage";
import { LivePage } from "./pages/LivePage";
import { SuggestionsPage } from "./pages/SuggestionsPage";
import { ImportPage } from "./pages/ImportPage";
import { Spinner } from "./components/shared/Spinner";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  string | undefined;

// The only Clerk org allowed to access this app. Must match wrangler.jsonc ALLOWED_ORG_IDS.
const ALLOWED_ORG_ID = "org_3EaJkxogJxHa3W9qpMZwlleFuSA";

/** Centered prompt shown in the content area when signed out. */
function SignedOutView() {
  const { openSignIn } = useClerk();
  return (
    <div style={signInWrapperStyles.container}>
      <div style={signInWrapperStyles.card}>
        <h2 style={signInWrapperStyles.title}>sign in to shelf</h2>
        <p style={signInWrapperStyles.subtitle}>adjacent document hub</p>
        <button style={signInWrapperStyles.button} onClick={() => openSignIn()}>
          sign in
        </button>
      </div>
    </div>
  );
}

/** Shown when the user is signed in but not a member of the allowed org. */
function DeniedView() {
  const { signOut } = useClerk();
  return (
    <div style={signInWrapperStyles.container}>
      <div style={signInWrapperStyles.card}>
        <h2 style={signInWrapperStyles.title}>access denied</h2>
        <p style={signInWrapperStyles.subtitle}>
          you are not a member of an authorized organization
        </p>
        <button style={signInWrapperStyles.button} onClick={() => signOut()}>
          sign out
        </button>
      </div>
    </div>
  );
}

type OrgGateState = "loading" | "denied" | "activating" | "allowed";

/**
 * Checks that the signed-in user is a member of the allowed Clerk org.
 * - If they are but the org isn't active, auto-activates it.
 * - If they aren't a member at all, shows a denial view.
 * - Waits for a token refresh after activating so the JWT carries org_id
 *   before any API calls are made (prevents the 403 the worker emits when
 *   org_id is missing from the token).
 */
function OrgGate({
  children,
  onReady,
}: {
  children: React.ReactNode;
  onReady: () => void;
}) {
  const { isLoaded, userMemberships, setActive } = useOrganizationList({
    userMemberships: { pageSize: 100 },
  });
  const { getToken } = useAuth();
  const [gateState, setGateState] = useState<OrgGateState>("loading");

  useEffect(() => {
    if (!isLoaded || !userMemberships.data) return;

    const isMember = userMemberships.data.some(
      (m) => m.organization.id === ALLOWED_ORG_ID,
    );

    if (!isMember) {
      setGateState("denied");
      return;
    }

    // They're a member. Wire the API token getter and proceed. We also try to
    // activate the org (best-effort) so the session token carries org_id for
    // the worker's fast-path check — but we do NOT block on it: the worker
    // independently verifies membership via the Clerk Backend API, so a member
    // is admitted even if setActive/getToken hiccups. This avoids wrongly
    // showing DeniedView to a real member.
    setGateState("activating");
    const finish = () => {
      setAuthTokenGetter(getToken);
      setGateState("allowed");
      onReady();
    };
    if (setActive) {
      setActive({ organization: ALLOWED_ORG_ID })
        .then(() => getToken())
        .catch(() => null)
        .finally(finish);
    } else {
      finish();
    }
  }, [isLoaded, userMemberships.data, setActive, getToken, onReady]);

  if (gateState === "loading" || gateState === "activating") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner
          size={28}
          label={gateState === "activating" ? "switching org…" : "loading…"}
        />
      </div>
    );
  }

  if (gateState === "denied") {
    return <DeniedView />;
  }

  return <>{children}</>;
}

/** Syncs the signed-in Clerk user's email into the local audit store so
 *  every client-side audit entry is attributed to the current actor. */
function ActorSync() {
  const { user } = useUser();
  useEffect(() => {
    const email = user?.primaryEmailAddress?.emailAddress;
    useAuditStore.getState().setActor(email);
  }, [user]);
  return null;
}

/** Inner gate that safely uses useAuth (only rendered when Clerk is configured) */
function ClerkAuthGate({
  children,
  onAuthReady,
}: {
  children: React.ReactNode;
  onAuthReady: () => void;
}) {
  const { isSignedIn, isLoaded } = useAuth();

  // While Clerk is loading, show nothing (prevents flash)
  if (!isLoaded) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner size={28} label="loading…" />
      </div>
    );
  }

  // Not signed in — show only a sign-in prompt (the surrounding Shell chrome
  // stays visible; no document content is rendered).
  if (!isSignedIn) {
    return <SignedOutView />;
  }

  // Signed in — now verify org membership before rendering content.
  // IMPORTANT: we do NOT set the API token getter until the OrgGate confirms
  // the org is active and the token is refreshed. This prevents the Sidebar
  // and page components from firing API calls with a token that lacks org_id.
  return (
    <OrgGate onReady={onAuthReady}>
      <ActorSync />
      {children}
    </OrgGate>
  );
}

/** Outer gate that conditionally applies Clerk auth */
function AuthGate({
  children,
  onAuthReady,
}: {
  children: React.ReactNode;
  onAuthReady?: () => void;
}) {
  if (!PUBLISHABLE_KEY) {
    // No Clerk — auth is always "ready"
    onAuthReady?.();
    return <>{children}</>;
  }
  return (
    <ClerkAuthGate onAuthReady={onAuthReady ?? (() => {})}>
      {children}
    </ClerkAuthGate>
  );
}

export default function App() {
  const [authReady, setAuthReady] = useState(!PUBLISHABLE_KEY);

  return (
    <ErrorBoundary>
      <Shell authReady={authReady}>
        <AuthGate onAuthReady={() => setAuthReady(true)}>
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route
              path="family/:slug/suggestions"
              element={<SuggestionsPage />}
            />
            <Route path="family/:slug" element={<FamilyPage />} />
            <Route path="doc/:pageId" element={<DocumentEditorPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route
              path="template-families"
              element={<TemplateFamiliesPage />}
            />
            <Route path="reviews" element={<ReviewQueuePage />} />
            <Route path="agent" element={<AgentSuggestionsPage />} />
            <Route path="snippets" element={<SnippetsPage />} />
            <Route path="publish/:family/:version" element={<PublishPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="sell-sheets" element={<SellSheetsPage />} />
            <Route path="live" element={<LivePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthGate>
      </Shell>
    </ErrorBoundary>
  );
}

const signInWrapperStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "70vh",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "var(--space-2)",
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    padding: "32px 40px",
  },
  title: {
    fontFamily: "var(--font-mono)",
    fontSize: 15,
    fontWeight: 600,
    color: "var(--fg-1)",
    margin: 0,
  },
  subtitle: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-3)",
    margin: "0 0 var(--space-3)",
  },
  button: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-on-deep)",
    background: "var(--comp-ink)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "8px 24px",
    cursor: "pointer",
  },
};
